import { useMemo, useState } from 'react'
import {
  format,
  startOfMonth,
  endOfMonth,
  eachDayOfInterval,
  isSameMonth,
  isSameDay,
  addMonths,
  subMonths,
  parseISO,
} from 'date-fns'
import { ChevronLeft, ChevronRight, CalendarDays } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { cn } from '@/lib/utils'

export interface LocalEventItem {
  id: string
  name: string
  start_date: string
  end_date: string | null
  event_type: string | null
  expected_impact: string
  venue: string | null
}

interface EventsCalendarProps {
  events: LocalEventItem[]
  cityLabel?: string | null
}

const IMPACT_STYLES: Record<string, string> = {
  high: 'bg-red-100 text-red-900 border-red-200',
  medium: 'bg-amber-50 text-amber-900 border-amber-200',
  low: 'bg-vesta-mist/60 text-vesta-navy/80 border-vesta-navy/10',
}

function eventSpansDay(event: LocalEventItem, day: Date): boolean {
  const start = parseISO(event.start_date + 'T00:00:00')
  const end = event.end_date
    ? parseISO(event.end_date + 'T00:00:00')
    : start
  const t = day.getTime()
  return t >= start.getTime() && t <= end.getTime()
}

export default function EventsCalendar({ events, cityLabel }: EventsCalendarProps) {
  const [month, setMonth] = useState(() => startOfMonth(new Date()))

  const days = useMemo(() => {
    const start = startOfMonth(month)
    const end = endOfMonth(month)
    return eachDayOfInterval({ start, end })
  }, [month])

  const upcomingHigh = useMemo(() => {
    const today = format(new Date(), 'yyyy-MM-dd')
    const in30 = format(addMonths(new Date(), 1), 'yyyy-MM-dd')
    return events.filter(
      (e) =>
        e.expected_impact === 'high' &&
        e.start_date >= today &&
        e.start_date <= in30,
    )
  }, [events])

  if (events.length === 0) {
    return (
      <div className="rounded-xl border border-dashed border-vesta-navy/15 bg-vesta-mist/20 px-4 py-8 text-center">
        <CalendarDays className="mx-auto mb-2 h-8 w-8 text-vesta-navy-muted" />
        <p className="text-sm font-medium text-vesta-navy">No events on file</p>
        <p className="mt-1 text-xs text-vesta-navy/70">
          {cityLabel
            ? `Add events for ${cityLabel} to get rate planning alerts.`
            : 'Import a CSV or add events manually on the Events page.'}
        </p>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {upcomingHigh.length > 0 && (
        <p className="text-sm text-vesta-navy/85">
          <span className="font-semibold text-vesta-navy">{upcomingHigh.length}</span> high-impact
          event{upcomingHigh.length !== 1 ? 's' : ''} in the next 30 days
        </p>
      )}

      <div className="flex items-center justify-between">
        <Button
          variant="outline"
          size="icon"
          className="h-8 w-8"
          onClick={() => setMonth((m) => subMonths(m, 1))}
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <span className="text-sm font-semibold text-vesta-navy">{format(month, 'MMMM yyyy')}</span>
        <Button
          variant="outline"
          size="icon"
          className="h-8 w-8"
          onClick={() => setMonth((m) => addMonths(m, 1))}
        >
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>

      <div className="grid grid-cols-7 gap-1 text-center text-[10px] font-semibold uppercase text-vesta-navy/55">
        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((d) => (
          <div key={d}>{d}</div>
        ))}
      </div>

      <div className="grid grid-cols-7 gap-1">
        {days.map((day) => {
          const dayEvents = events.filter((e) => eventSpansDay(e, day))
          const hasHigh = dayEvents.some((e) => e.expected_impact === 'high')
          return (
            <div
              key={day.toISOString()}
              className={cn(
                'min-h-[52px] rounded-md border p-1 text-left',
                isSameMonth(day, month) ? 'border-vesta-navy/8 bg-white' : 'border-transparent bg-vesta-mist/30 opacity-50',
                isSameDay(day, new Date()) && 'ring-1 ring-vesta-gold/50',
                hasHigh && 'bg-red-50/50',
              )}
            >
              <span className="text-[10px] font-medium text-vesta-navy/70">{format(day, 'd')}</span>
              {dayEvents.slice(0, 2).map((e) => (
                <div
                  key={e.id}
                  className="mt-0.5 truncate rounded px-0.5 text-[9px] leading-tight text-vesta-navy"
                  title={e.name}
                >
                  {e.name}
                </div>
              ))}
            </div>
          )
        })}
      </div>

      <ul className="space-y-2 border-t border-vesta-navy/10 pt-3">
        {events
          .filter((e) => {
            const mStart = format(startOfMonth(month), 'yyyy-MM-dd')
            const mEnd = format(endOfMonth(month), 'yyyy-MM-dd')
            return e.start_date <= mEnd && (e.end_date ?? e.start_date) >= mStart
          })
          .slice(0, 8)
          .map((e) => (
            <li key={e.id} className="flex items-start justify-between gap-2 text-sm">
              <div>
                <p className="font-medium text-vesta-navy">{e.name}</p>
                <p className="text-xs text-vesta-navy/65">
                  {e.start_date}
                  {e.end_date && e.end_date !== e.start_date ? ` – ${e.end_date}` : ''}
                  {e.venue ? ` · ${e.venue}` : ''}
                </p>
              </div>
              <Badge className={cn('shrink-0 text-[10px]', IMPACT_STYLES[e.expected_impact] ?? IMPACT_STYLES.medium)}>
                {e.expected_impact}
              </Badge>
            </li>
          ))}
      </ul>
    </div>
  )
}
